import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  Users,
  DollarSign,
  BarChart2,
  QrCode,
  ArrowRight,
  Send,
  MessageSquare,
  Bot,
  Building2,
  Zap,
  Radio,
  ShieldCheck,
  RefreshCw,
} from "lucide-react";
import {
  getOverviewStats,
  listConversations,
  type CurrentUser,
  type Conversation,
} from "../../lib/api";

export function OverviewView({ token, user }: { token: string; user: CurrentUser }) {
  const navigate = useNavigate();
  const isSuperAdmin = user.role === "superadmin" || user.role === "platform_admin";

  // Live Stats Query
  const overviewQuery = useQuery({
    queryKey: ["overview-stats", user.tenant_id, user.role],
    queryFn: () => getOverviewStats(token, user.tenant_id),
    refetchInterval: 5_000,
  });

  // Live Recent Conversations Query
  const recentChatsQuery = useQuery({
    queryKey: ["inbox", "recent-conversations", user.tenant_id],
    queryFn: () => listConversations(token, undefined, user.tenant_id),
    refetchInterval: 5_000,
  });

  const stats = overviewQuery.data;
  const recentChats = (recentChatsQuery.data || []).slice(0, 5);

  const totalUsers = stats?.platform_users ?? 2;
  const totalInstances = stats?.total_instances ?? stats?.qr_instances ?? 1;
  const connectedInstances = stats?.connected_instances ?? stats?.connected_qr_instances ?? 1;
  const totalTenants = stats?.tenants ?? 1;
  const totalContacts = stats?.total_contacts ?? 0;
  const totalCampaigns = stats?.total_campaigns ?? 0;
  const totalCredits = stats?.wallet_credits ?? 5000;
  const todaySent = stats?.today_sent ?? 0;

  return (
    <div className="space-y-6">
      {/* 1. Header & Quick Workspace Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            {isSuperAdmin ? (
              <>
                <Building2 className="w-6 h-6 text-purple-600" />
                <span>Super Admin Control Plane</span>
              </>
            ) : (
              <>
                <Radio className="w-6 h-6 text-emerald-600" />
                <span>Organization Dashboard</span>
              </>
            )}
            {(overviewQuery.isFetching || recentChatsQuery.isFetching) && (
              <span className="flex items-center gap-1 text-[11px] font-normal text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                <RefreshCw className="w-3 h-3 animate-spin" />
                <span>Syncing live...</span>
              </span>
            )}
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            {isSuperAdmin
              ? "Oversee all organizations, manage instance quotas, and inspect platform health."
              : `Active workspace for ${stats?.tenant_name || "Interlink Consultant"} (919323818985)`}
          </p>
        </div>

        {/* Quick Actions */}
        <div className="flex items-center gap-2">
          <Link
            to="/admin/inbox"
            className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5 shadow-2xs"
          >
            <MessageSquare className="w-3.5 h-3.5 text-slate-500" />
            <span>Open Live Inbox</span>
          </Link>

          <Link
            to="/admin/campaigns"
            className="btn-primary text-xs py-2 px-3.5 flex items-center gap-1.5 shadow-2xs"
          >
            <Send className="w-3.5 h-3.5" />
            <span>New Broadcast</span>
          </Link>
        </div>
      </div>

      {/* 2. Top Metric Cards (Matches eNotify exact dashboard layout) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3.5">
        {isSuperAdmin ? (
          <>
            {/* Super Admin Card 1: Organizations */}
            <div className="enotify-metric-card">
              <div className="flex items-start justify-between">
                <span className="text-xs font-semibold text-slate-600">Total Tenants</span>
                <Building2 className="w-4 h-4 text-purple-500" />
              </div>
              <div className="text-3xl font-bold text-slate-900 mt-2">{totalTenants}</div>
              <Link to="/admin/tenants" className="metric-link">
                <span>View</span> <ArrowRight className="w-3 h-3" />
              </Link>
            </div>

            {/* Super Admin Card 2: Platform Users */}
            <div className="enotify-metric-card">
              <div className="flex items-start justify-between">
                <span className="text-xs font-semibold text-slate-600">Platform Users</span>
                <Users className="w-4 h-4 text-slate-400" />
              </div>
              <div className="text-3xl font-bold text-slate-900 mt-2">{totalUsers}</div>
              <Link to="/admin/team" className="metric-link">
                <span>View</span> <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </>
        ) : (
          <>
            {/* Tenant Card 1: Audience Contacts */}
            <div className="enotify-metric-card">
              <div className="flex items-start justify-between">
                <span className="text-xs font-semibold text-slate-600">CRM Contacts</span>
                <Users className="w-4 h-4 text-emerald-500" />
              </div>
              <div className="text-3xl font-bold text-slate-900 mt-2">{totalContacts}</div>
              <Link to="/admin/contacts" className="metric-link">
                <span>Audience</span> <ArrowRight className="w-3 h-3" />
              </Link>
            </div>

            {/* Tenant Card 2: Broadcast Campaigns */}
            <div className="enotify-metric-card">
              <div className="flex items-start justify-between">
                <span className="text-xs font-semibold text-slate-600">Campaigns</span>
                <Send className="w-4 h-4 text-emerald-500" />
              </div>
              <div className="text-3xl font-bold text-slate-900 mt-2">{totalCampaigns}</div>
              <Link to="/admin/campaigns" className="metric-link">
                <span>Broadcasts</span> <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </>
        )}

        {/* Card 3: WhatsApp Instances */}
        <div className="enotify-metric-card">
          <div className="flex items-start justify-between">
            <span className="text-xs font-semibold text-slate-600">QR Instances</span>
            <QrCode className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="text-3xl font-bold text-slate-900 mt-2">
            {connectedInstances} <span className="text-xs text-slate-400 font-normal">/ {totalInstances}</span>
          </div>
          <Link to="/admin/qr-instances" className="metric-link">
            <span>Pair Node</span> <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        {/* Card 4: Wallet Credits */}
        <div className="enotify-metric-card">
          <div className="flex items-start justify-between">
            <span className="text-xs font-semibold text-slate-600">Wallet Credits</span>
            <DollarSign className="w-4 h-4 text-amber-500" />
          </div>
          <div className="text-3xl font-bold text-slate-900 mt-2">{totalCredits.toLocaleString()}</div>
          <Link to="/admin/billing" className="metric-link">
            <span>Recharge</span> <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        {/* Card 5: Today Messages */}
        <div className="enotify-metric-card">
          <div className="flex items-start justify-between">
            <span className="text-xs font-semibold text-slate-600">Messages Today</span>
            <BarChart2 className="w-4 h-4 text-sky-500" />
          </div>
          <div className="text-3xl font-bold text-slate-900 mt-2">{todaySent}</div>
          <Link to="/admin/delivery-reports" className="metric-link">
            <span>Reports</span> <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        {/* Card 6: Smart Automations */}
        <div className="enotify-metric-card">
          <div className="flex items-start justify-between">
            <span className="text-xs font-semibold text-slate-600">Auto-Bots</span>
            <Bot className="w-4 h-4 text-purple-500" />
          </div>
          <div className="text-3xl font-bold text-slate-900 mt-2">Active</div>
          <Link to="/admin/automations" className="metric-link">
            <span>Rules</span> <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>

      {/* 3. Main Dashboard Widgets Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        
        {/* Left Column: Recent WhatsApp Conversations Feed (7 Cols) */}
        <div className="lg:col-span-7 bg-white border border-slate-200 rounded-xl p-5 shadow-2xs space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-emerald-600" />
              <span>Live Omnichannel Chats</span>
            </h3>
            <Link to="/admin/inbox" className="text-xs font-bold text-emerald-600 hover:underline">
              View All Chats &rarr;
            </Link>
          </div>

          {recentChats.length === 0 ? (
            <div className="text-center py-10 text-xs text-slate-400 space-y-2">
              <p>No active conversations yet.</p>
              <Link
                to="/admin/inbox"
                className="btn-primary text-xs py-1.5 px-3 inline-flex items-center gap-1.5 mx-auto"
              >
                <span>Start New Conversation</span>
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {recentChats.map((conv: Conversation) => {
                const isMeta = conv.channel_type === "meta_official";
                const contactName =
                  conv.contact?.first_name ||
                  conv.contact?.phone_e164 ||
                  "WhatsApp Contact";

                return (
                  <div
                    key={conv.id}
                    onClick={() => navigate("/admin/inbox")}
                    className="py-3 flex items-center justify-between hover:bg-slate-50 px-2 rounded-lg cursor-pointer transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-800 font-bold text-xs flex items-center justify-center shrink-0">
                        {contactName.slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-bold text-slate-900 text-xs flex items-center gap-1.5">
                          {contactName}
                          {isMeta ? (
                            <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[9px] font-semibold border border-blue-200">
                              Meta
                            </span>
                          ) : (
                            <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[9px] font-semibold border border-emerald-200">
                              QR Node
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 truncate max-w-xs">
                          {conv.last_message_preview || "No messages yet"}
                        </p>
                      </div>
                    </div>

                    <div className="text-right">
                      <span className="text-[10px] font-mono text-slate-400 block">
                        {new Date(conv.last_message_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </span>
                      {conv.unread_count > 0 && (
                        <span className="px-1.5 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold">
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right Column: Connection Health & Quick Broadcast Launcher (5 Cols) */}
        <div className="lg:col-span-5 space-y-5">
          {/* Connection Status Card */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-2xs space-y-3">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-500" />
              <span>Session Health &amp; Security</span>
            </h3>

            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2.5 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Encryption Protocol:</span>
                <span className="font-mono font-bold text-emerald-700 flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5" /> Signal Double Ratchet
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">WebSocket Multi-Device:</span>
                <span className="status-pill status-pill--connected text-[10px]">
                  Online &amp; Synced
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Anti-Ban Pacing Guard:</span>
                <span className="font-semibold text-slate-800">Enabled (30 msgs/min)</span>
              </div>
            </div>

            <div className="pt-2 flex justify-between items-center text-xs">
              <Link to="/admin/qr-instances" className="text-emerald-600 font-bold hover:underline">
                Manage Instances &rarr;
              </Link>
              <Link to="/admin/delivery-reports" className="text-slate-500 hover:text-slate-900">
                Audit Logs
              </Link>
            </div>
          </div>

          {/* Quick Launch Broadcast Banner */}
          <div className="bg-linear-to-br from-emerald-600 to-teal-700 rounded-xl p-5 text-white shadow-2xs space-y-3">
            <div className="flex items-center gap-2">
              <Send className="w-5 h-5 text-emerald-200" />
              <h3 className="text-sm font-bold">Launch WhatsApp Broadcast</h3>
            </div>
            <p className="text-xs text-emerald-100 leading-relaxed">
              Reach your segmented CRM audience with personalized announcements, templates, and media attachments.
            </p>
            <Link
              to="/admin/campaigns"
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-white text-emerald-900 font-bold text-xs hover:bg-emerald-50 transition-colors shadow-sm"
            >
              <span>Create Campaign</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
